import { useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, X, Send, Sparkles, MessageCircle } from "lucide-react";

const STORAGE_KEY = "hw_concierge_history_v1";

const SUGGESTED = [
  "Which hotel should I pick for my budget?",
  "Is this bid a good deal?",
  "Suggest a fair counter price",
  "Best area to stay for a family?",
];

export default function TravellerConcierge() {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-40)));
    } catch { /* ignore */ }
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  // Iter26 — listen for the global "swaan:open-mythri" event so the Support
  // Centre's "Ask Mythri AI" card can pop the widget open on click.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("swaan:open-mythri", handler);
    return () => window.removeEventListener("swaan:open-mythri", handler);
  }, []);

  // Only show for customer role, and never on landing/login/register pages
  const hidden = !user || user.role !== "customer" ||
    /^\/(login|register|hotels-portal|ops|travellers|partner-cta|onboarding|auth\/callback|$)/.test(location.pathname) ||
    location.pathname === "/";

  if (hidden) return null;

  // Detect current context from URL
  const getContext = () => {
    const ctx = {};
    // On booking request detail: /requests/:requestId
    if (/^\/requests\/[^/]+$/.test(location.pathname) && params.requestId) {
      ctx.request_id = params.requestId;
    }
    // On hotel detail: /hotels/:hotelId
    if (/^\/hotels\/[^/]+$/.test(location.pathname) && params.hotelId) {
      ctx.hotel_id = params.hotelId;
    }
    // Search page — pull city + area from query string
    if (location.pathname === "/search") {
      const search = new URLSearchParams(location.search);
      if (search.get("city")) ctx.city = search.get("city");
      if (search.get("area")) ctx.area = search.get("area");
    }
    return ctx;
  };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const nextHistory = [...messages, { role: "user", text: q, ts: Date.now() }];
    setMessages(nextHistory);
    setBusy(true);
    try {
      const ctx = getContext();
      const { data } = await api.post("/ai/traveller-chat", {
        message: q,
        history: nextHistory.slice(-8),
        ...ctx,
      });
      setMessages([...nextHistory, { role: "assistant", text: data.reply || "", ts: Date.now(), fallback: data.fallback }]);
    } catch (e) {
      setMessages([
        ...nextHistory,
        {
          role: "assistant",
          text: "I couldn't reach my brain just now. Please try again in a moment — meanwhile, remember: aim for 10-20% below the current quote when countering.",
          ts: Date.now(),
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 pl-3 pr-4 py-3 rounded-full text-white bg-gradient-to-br from-emerald-600 via-emerald-600 to-blue-700 hover:from-emerald-500 hover:to-blue-600 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
          data-testid="concierge-launcher"
          aria-label="Open Mythri AI"
        >
          <span className="relative">
            <Bot className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-white animate-pulse" />
          </span>
          <span className="hidden sm:inline font-bold text-sm">Hi Mythri AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[92vw] sm:w-[420px] max-h-[min(680px,calc(100vh-6rem))] flex flex-col rounded-md overflow-hidden shadow-2xl border border-slate-200 bg-white"
          data-testid="concierge-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-blue-700 text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-white/15 grid place-items-center backdrop-blur">
              <Bot className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="hw-head font-black text-lg leading-tight" data-testid="concierge-title">Mythri AI</div>
              <div className="text-xs text-white/80 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Your money-saving well-wisher
              </div>
            </div>
            <button onClick={clearChat} className="text-xs text-white/70 hover:text-white font-bold px-2 py-1 rounded-sm hover:bg-white/10" data-testid="concierge-clear">
              Clear
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white p-1 rounded-sm hover:bg-white/10"
              data-testid="concierge-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="concierge-messages">
            {messages.length === 0 && (
              <div className="text-sm text-slate-600 leading-relaxed">
                <div className="hw-head font-bold text-slate-900 text-base mb-1">Namaste, {user.name?.split(" ")[0]}! I&apos;m Mythri.</div>
                Think of me as your travel well-wisher — that street-smart friend who&apos;s stayed in hundreds of hotels and knows every bargaining trick. Ask me anything about picking hotels, comparing bids, or striking the best deal.
                <div className="mt-4 space-y-2">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      className="w-full text-left text-sm bg-white border border-slate-200 hover:border-blue-500 hover:bg-blue-50 px-3 py-2 rounded-sm transition-colors"
                      data-testid={`concierge-suggestion-${s.slice(0, 10).replace(/\s/g, "-")}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5 inline mr-1.5 text-blue-700" />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`concierge-msg-${m.role}-${i}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-md text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-blue-700 text-white rounded-tr-sm"
                      : m.error
                        ? "bg-amber-50 border border-amber-200 text-amber-900 rounded-tl-sm"
                        : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"
                  }`}
                >
                  {m.role === "assistant" && (
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-700 mb-1">
                      <Bot className="w-3 h-3" /> Mythri{m.fallback && <span className="text-slate-400">· offline hint</span>}
                    </div>
                  )}
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 text-slate-500 text-sm px-3 py-2 rounded-md flex items-center gap-2" data-testid="concierge-typing">
                  <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              ask();
            }}
            className="p-3 border-t border-slate-200 bg-white flex gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about hotels, bids, or negotiation…"
              className="rounded-sm"
              data-testid="concierge-input"
              disabled={busy}
              autoFocus
            />
            <Button
              type="submit"
              disabled={busy || !input.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold"
              data-testid="concierge-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
